import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Ticker from "@/components/site/Ticker";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { toast } from "sonner";

const KIND_LABEL = {
  report: "PDF Report",
  article: "Sponsored Article",
  subscription: "Subscription",
  job: "Job Board",
};

export default function StorePage() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    email: "", brand_name: "", tagline: "", link_url: "",
    job_title: "", job_description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/store/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const groups = ["report", "article", "subscription", "job"];
  const byKind = groups.map((k) => ({
    kind: k,
    items: products.filter((p) => p.kind === k),
  }));

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buy = async (e) => {
    e.preventDefault();
    if (!selected || !form.email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/store/checkout", {
        product_id: selected.id,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Checkout failed", { description: err?.response?.data?.detail || "Try again." });
      setBusy(false);
    }
  };

  const requiresBrand = selected && (selected.kind === "article" || selected.kind === "job");
  const requiresJobFields = selected && selected.kind === "job";

  return (
    <div data-testid="store-page" className="bg-white text-[#0a0a0a]">
      <Seo
        title="Store — Universtrend"
        description="Buy trend reports, sponsor an article, subscribe to Monday Index Pro, or post a job on the Clip Radar board."
      />
      <Header />
      <Ticker />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="overline text-[#52525b] mb-4">Store · Live payments</div>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl lg:text-7xl">
            Everything worth<br /><span className="text-[#ff3b30]">buying, from us.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-[#52525b]">
            Trend reports, sponsored articles, premium newsletter and clipper job posts — all delivered under 24h. Instant Stripe checkout.
          </p>
        </div>
      </section>

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 space-y-12">
          {byKind.map(({ kind, items }) => items.length === 0 ? null : (
            <div key={kind}>
              <div className="flex items-baseline justify-between mb-4 border-b border-black pb-2">
                <h2 className="font-display font-extrabold text-2xl lg:text-4xl tracking-tighter">
                  {KIND_LABEL[kind]}
                </h2>
                <span className="font-mono text-xs text-[#52525b]">{items.length} option{items.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-black">
                {items.map((p) => (
                  <article
                    key={p.id}
                    data-testid={`store-product-${p.id}`}
                    className="border-r border-b border-black p-6 flex flex-col"
                  >
                    <div className="overline text-[#52525b]">{KIND_LABEL[p.kind]}</div>
                    <h3 className="font-display font-extrabold text-xl tracking-tight mt-2 min-h-[3.5rem]">{p.label}</h3>
                    <p className="text-sm text-[#52525b] mt-3 flex-1">{p.description}</p>
                    <div className="mt-6 flex items-baseline justify-between border-t border-black/15 pt-4">
                      <span className="font-display font-extrabold text-3xl">${p.amount.toFixed(0)}</span>
                      <button
                        data-testid={`store-buy-${p.id}`}
                        onClick={() => setSelected(p)}
                        className="hard-shadow overline border border-black bg-black text-white px-4 py-2 hover:bg-[#ff3b30]"
                      >
                        Buy →
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {selected && (
        <div
          data-testid="store-buy-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => !busy && setSelected(null)}
        >
          <form
            onSubmit={buy}
            className="bg-white border border-black max-w-lg w-full p-6 lg:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overline text-[#52525b] mb-2">Checkout</div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight">{selected.label}</h3>
            <div className="mt-2 font-mono text-sm">
              <b className="text-2xl font-display">${selected.amount.toFixed(0)}</b> {selected.currency.toUpperCase()}
            </div>

            <label className="overline block mt-5 mb-1">Email (receipt + delivery)</label>
            <input
              data-testid="store-form-email"
              type="email"
              required
              value={form.email}
              onChange={onChange("email")}
              placeholder="you@company.com"
              className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
            />

            {requiresBrand && (
              <>
                <label className="overline block mt-4 mb-1">Brand / Company</label>
                <input
                  data-testid="store-form-brand"
                  type="text"
                  required
                  value={form.brand_name}
                  onChange={onChange("brand_name")}
                  maxLength={60}
                  placeholder="Nike"
                  className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
                />
                <label className="overline block mt-4 mb-1">Link URL</label>
                <input
                  data-testid="store-form-link"
                  type="url"
                  required
                  value={form.link_url}
                  onChange={onChange("link_url")}
                  placeholder="https://your-brand.com"
                  className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
                />
              </>
            )}

            {requiresJobFields && (
              <>
                <label className="overline block mt-4 mb-1">Job title</label>
                <input
                  data-testid="store-form-job-title"
                  type="text"
                  required
                  value={form.job_title}
                  onChange={onChange("job_title")}
                  maxLength={100}
                  placeholder="Clipper for daily podcast highlights"
                  className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
                />
                <label className="overline block mt-4 mb-1">Description (short)</label>
                <textarea
                  data-testid="store-form-job-desc"
                  required
                  value={form.job_description}
                  onChange={onChange("job_description")}
                  maxLength={800}
                  rows={4}
                  placeholder="Rate, deliverables, timeline, how to apply…"
                  className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none resize-none"
                />
              </>
            )}

            {selected.kind === "article" && (
              <>
                <label className="overline block mt-4 mb-1">Tagline (shown on homepage)</label>
                <input
                  data-testid="store-form-tagline"
                  type="text"
                  value={form.tagline}
                  onChange={onChange("tagline")}
                  maxLength={80}
                  placeholder="Just Search It."
                  className="w-full border border-black px-3 py-2.5 font-mono text-sm focus:outline-none"
                />
              </>
            )}

            <button
              data-testid="store-checkout-button"
              type="submit"
              disabled={busy}
              className="mt-6 w-full bg-black text-white py-3 overline hover:bg-[#ff3b30] disabled:opacity-50"
            >
              {busy ? "Redirecting to Stripe…" : `Pay $${selected.amount.toFixed(0)} →`}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setSelected(null)}
              className="mt-3 w-full text-center font-mono text-xs text-[#52525b] hover:text-black"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      <Footer />
    </div>
  );
}
